import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import {
  ClockService,
  DATE_INPUT_FORMAT,
  DateRangePipe,
} from '@techspert-io/schedule-calendar';
import moment from 'moment-timezone';
import { TimezonePreferenceService } from '../../../../../../shared/services/timezone-preference.service';

export enum ExpertAvailabilityCalendarView {
  Week = 'week',
  Day = 'day',
}

const VIEW_DAY_COUNTS: Record<ExpertAvailabilityCalendarView, number> = {
  [ExpertAvailabilityCalendarView.Week]: 7,
  [ExpertAvailabilityCalendarView.Day]: 1,
};

/**
 * Navigates the days from today, a week or a day at a time. Today is the
 * earliest navigable day.
 */
@Component({
  selector: 'app-expert-availability-header',
  templateUrl: './expert-availability-header.component.html',
  styleUrl: './expert-availability-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DateRangePipe,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    ReactiveFormsModule,
  ],
})
export class ExpertAvailabilityHeaderComponent {
  /** Minutes long of the expert's calls */
  readonly callDuration = input.required<number>();

  readonly timezoneName = inject(TimezonePreferenceService).selectedTimezone;

  private readonly clock = inject(ClockService);

  readonly calendarView = new FormControl<ExpertAvailabilityCalendarView>(
    ExpertAvailabilityCalendarView.Week,
    { nonNullable: true }
  );

  readonly selectedView = toSignal(this.calendarView.valueChanges, {
    initialValue: this.calendarView.value,
  });

  /** Days from today, which is the earliest navigable day */
  private readonly dayOffset = signal(0);

  private readonly dayCount = computed(
    () => VIEW_DAY_COUNTS[this.selectedView()]
  );

  /**
   * Start of the first selected day in the timezone, following the clock so
   * today moves on at midnight
   */
  private readonly start = computed(() =>
    moment(this.clock.now())
      .tz(this.timezoneName())
      .startOf('day')
      .add(this.dayOffset(), 'days')
  );

  /** YYYY-MM-DD dates of the selected days in the timezone */
  readonly selectedDates = computed(() =>
    Array.from({ length: this.dayCount() }).map((_, i) =>
      this.start().clone().add(i, 'days').format(DATE_INPUT_FORMAT)
    )
  );

  /**
   * As date strings, which the date range pipe shows as calendar dates rather
   * than shifting them into the browser's timezone.
   */
  readonly selectedDateRange = computed(() => ({
    start: this.selectedDates().at(0),
    end: this.selectedDates().at(-1),
  }));

  readonly isToday = computed(() => this.dayOffset() === 0);

  get ExpertAvailabilityCalendarView() {
    return ExpertAvailabilityCalendarView;
  }

  navigateBack() {
    this.dayOffset.update((offset) => Math.max(0, offset - this.dayCount()));
  }

  navigateForward() {
    this.dayOffset.update((offset) => offset + this.dayCount());
  }

  navigateToToday() {
    this.dayOffset.set(0);
  }
}
